import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const OperatorIdentityIdSchema = Type.String({ minLength: 1, maxLength: 512 });
const OperatorIdentityFields = {
  id: OperatorIdentityIdSchema,
  tenantId: OperatorIdentityIdSchema,
  storeId: OperatorIdentityIdSchema,
  login: Type.String({ minLength: 1, maxLength: 64 }),
  displayName: Type.String({ minLength: 1, maxLength: 120 }),
  role: Type.Union([
    Type.Literal("CASHIER"),
    Type.Literal("MANAGER"),
    Type.Literal("OWNER"),
    Type.Literal("WAITER"),
    Type.Literal("COACH"),
  ]),
  coachProfileId: Type.Union([OperatorIdentityIdSchema, Type.Null()]),
};

export const OperatorIdentitySchema = Type.Object(
  {
    ...OperatorIdentityFields,
    expiresAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type OperatorIdentity = Static<typeof OperatorIdentitySchema>;

export const OperatorSessionContextSchema = Type.Union([
  Type.Object(
    {
      mode: Type.Literal("PERSONAL"),
      shiftId: Type.Null(),
      shiftStatus: Type.Null(),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      mode: Type.Literal("SHARED_SHIFT"),
      shiftId: Type.Null(),
      shiftStatus: Type.Null(),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      mode: Type.Literal("SHARED_SHIFT"),
      shiftId: OperatorIdentityIdSchema,
      shiftStatus: Type.Union([Type.Literal("OPEN"), Type.Literal("HANDOVER_PENDING")]),
    },
    { additionalProperties: false },
  ),
]);
export type OperatorSessionContext = Static<typeof OperatorSessionContextSchema>;

const ActiveOperatorSessionContextSchema = Type.Union([
  Type.Object(
    {
      mode: Type.Literal("PERSONAL"),
      shiftId: Type.Null(),
      shiftStatus: Type.Null(),
      activationState: Type.Literal("ACTIVE"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      mode: Type.Literal("SHARED_SHIFT"),
      shiftId: Type.Null(),
      shiftStatus: Type.Null(),
      activationState: Type.Literal("ACTIVE"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      mode: Type.Literal("SHARED_SHIFT"),
      shiftId: OperatorIdentityIdSchema,
      shiftStatus: Type.Union([Type.Literal("OPEN"), Type.Literal("HANDOVER_PENDING")]),
      activationState: Type.Literal("ACTIVE"),
    },
    { additionalProperties: false },
  ),
]);

const SimulatedOperatorIdentitySchema = Type.Object(
  { ...OperatorIdentityFields, expiresAt: Type.Null() },
  { additionalProperties: false },
);
const StoreAcceptedOperatorIdentitySchema = Type.Object(
  { ...OperatorIdentityFields, expiresAt: Type.String({ format: "date-time" }) },
  { additionalProperties: false },
);

export const OperatorSessionReadModelSchema = Type.Union([
  Type.Object(
    {
      operator: SimulatedOperatorIdentitySchema,
      evidence: Type.Literal("SIMULATED"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      operator: StoreAcceptedOperatorIdentitySchema,
      session: ActiveOperatorSessionContextSchema,
      evidence: Type.Literal("STORE_ACCEPTED"),
    },
    { additionalProperties: false },
  ),
]);
export type OperatorSessionReadModel = Static<typeof OperatorSessionReadModelSchema>;

export function isOperatorSessionReadModel(value: unknown): value is OperatorSessionReadModel {
  return Value.Check(OperatorSessionReadModelSchema, value);
}
